import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';
import { rewriteSearchQuery } from '@theme/search-query-rewriter';
import { resolveBrandCollectionUrl } from '@theme/brand-collection-url';

/**
 * A custom element that allows the user to clean a search input.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchPageInput - The search input element.
 * @extends {Component<Refs>}
 */
class SearchPageInputComponent extends Component {
  requiredRefs = ['searchPageInput'];

  #skipNextFocus = false;

  connectedCallback() {
    super.connectedCallback();
    this.refs.searchPageInput.form?.addEventListener('submit', this.#handleFormSubmit);
    this.refs.searchPageInput.addEventListener('focus', this.#handleFocus);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.searchPageInput.form?.removeEventListener('submit', this.#handleFormSubmit);
    this.refs.searchPageInput.removeEventListener('focus', this.#handleFocus);
  }

  /**
   * Handles the keydown event on the search input and resets the search when
   * empty and Escape is pressed.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown = debounce((event) => {
    const value = this.refs.searchPageInput.value.trim();

    if (event.key === 'Escape' && value === '') {
      this.#submitEmptySearch();
    }
  }, 100);

  /**
   * Clears the search input value without navigating.
   * @param {Event} event - The click event.
   */
  clearInput = (event) => {
    event.preventDefault();
    const searchInput = this.refs.searchPageInput;
    if (window.GBTrack) {
      window.GBTrack.track('search_input_clear', {
        tracking_id: this.dataset.trackingId || '',
        keyword: searchInput.value || '',
      });
    }
    searchInput.value = '';
    this.#skipNextFocus = true;
    searchInput.focus();
  };

  #handleFormSubmit = (event) => {
    event.preventDefault();
    const form = event.target;
    const params = new URLSearchParams(new FormData(form));
    const rawKeyword = this.refs.searchPageInput.value || '';
    params.set('q', rewriteSearchQuery(rawKeyword));
    if (window.GBTrack) {
      window.GBTrack.track('search_input_submit', {
        tracking_id: this.dataset.trackingId || '',
        keyword: rawKeyword,
      });
    }

    // 브랜드명을 검색한 경우 /search 를 거치지 않고 브랜드 컬렉션으로 바로 보낸다.
    const brandCollectionUrl = resolveBrandCollectionUrl(rawKeyword);
    if (brandCollectionUrl) {
      window.location.replace(brandCollectionUrl);
      return;
    }

    window.location.replace(`${form.action}?${params.toString()}`);
  };

  #submitEmptySearch() {
    const searchInput = this.refs.searchPageInput;

    this.#skipNextFocus = true;
    searchInput.focus();
    searchInput.value = '';

    if (this.#isEmptyState()) return;

    const form = searchInput.form;
    if (!form) return;
    const params = new URLSearchParams(new FormData(form));
    window.location.replace(`${form.action}?${params.toString()}`);
  }

  #isEmptyState = () => {
    const url = new URL(window.location.href);
    const queryParam = url.searchParams.get('q') ?? '';

    return queryParam.trim() === '';
  };

  /**
   * When the search page input is focused, open the search modal
   * and transfer the current search term to continue typing there
   * with predictive search.
   */
  #handleFocus = () => {
    if (this.#skipNextFocus) {
      this.#skipNextFocus = false;
      return;
    }

    const searchModal = document.querySelector('#search-modal');
    if (!searchModal || typeof searchModal.showDialog !== 'function') return;

    const currentValue = this.refs.searchPageInput.value;
    this.refs.searchPageInput.blur();

    searchModal.showDialog();

    // Wait for the modal to fully open and its input to be focused,
    // then transfer the current search term
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const modalInput = searchModal.querySelector('input[name="q"]');
        if (!(modalInput instanceof HTMLInputElement)) return;

        modalInput.value = currentValue;
        modalInput.setSelectionRange(currentValue.length, currentValue.length);
        modalInput.focus();

        if (currentValue.trim()) {
          // Show the reset button
          const predictiveSearch = searchModal.querySelector('predictive-search-component');
          const resetButton = predictiveSearch?.querySelector('[ref="resetButton"]');
          if (resetButton) resetButton.hidden = false;

          // Trigger predictive search
          modalInput.dispatchEvent(new InputEvent('input', {
            inputType: 'insertText',
            bubbles: true,
          }));
        }
      });
    });
  };
}

if (!customElements.get('search-page-input-component')) {
  customElements.define('search-page-input-component', SearchPageInputComponent);
}
